import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronUp, MessageSquare, Sparkles, X } from "lucide-react";
import { AgentMarkdown } from "./AgentMarkdown";

interface Props {
  voix: string;
  details: string;
  onOpenDrawer: () => void;
  onDismiss: () => void;
}

/**
 * Inline reply card shown under the agent input after each turn.
 *
 * Pattern: Alexa-style two-layer reply. VOIX (spoken) is shown as a
 * pull-quote ONLY when distinct from DETAILS (i.e. the model
 * complied). DETAILS is always rendered as proper markdown — bold,
 * bullets, links, code — never as raw asterisks. When the rendered
 * body is taller than 12rem, we clamp it with a fade-out gradient and
 * surface a "Voir la réponse complète →" button that opens the chat
 * drawer (where it can scroll properly without pushing the panneaux
 * grid down).
 */
const COLLAPSED_HEIGHT_PX = 192; // ~12rem; ~7-8 lines of prose-sm

export function LastReplyCard({ voix, details, onOpenDrawer, onDismiss }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // VOIX gets pull-quote treatment only when it's a real layered
  // response (model complied). When the parser fell back, voix ===
  // details and we skip the pull-quote — the body alone is the message.
  const voixDistinct =
    voix && details && voix.trim() !== details.trim();

  // Reset on every fresh reply (same instance reused across turns).
  useEffect(() => {
    setExpanded(false);
    setCollapsed(false);
  }, [voix, details]);

  // Measure rendered body height. useLayoutEffect runs after the DOM
  // is laid out but before paint, so the user never sees the
  // unclipped tall version flash.
  useLayoutEffect(() => {
    if (!bodyRef.current) return;
    setOverflowing(bodyRef.current.scrollHeight > COLLAPSED_HEIGHT_PX + 8);
  }, [voix, details, expanded]);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="w-full max-w-6xl mx-auto mt-2 px-4 py-1.5 flex items-center gap-2 text-xs text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
      >
        <Sparkles className="h-3 w-3 text-chip-orange shrink-0" />
        <span className="line-clamp-2 flex-1 text-left italic">« {voix} »</span>
        <span className="shrink-0">Réafficher</span>
      </button>
    );
  }

  const clamped = overflowing && !expanded;

  return (
    <section
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="max-w-6xl mx-auto mt-2 mb-1 mx-4 sm:mx-auto px-4 py-3 rounded-xl bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 shadow-sm"
      aria-label="Dernière réponse de l'agent"
    >
      <header className="flex items-center gap-2 text-xs">
        <Sparkles className="h-3.5 w-3.5 text-chip-orange shrink-0" />
        <span className="font-medium text-ink dark:text-ink-dark">Agent</span>
        <span className="text-muted dark:text-muted-dark">·</span>
        <span className="text-muted dark:text-muted-dark">à l'instant</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onOpenDrawer}
          className="inline-flex items-center gap-1 text-ink dark:text-ink-dark hover:underline"
        >
          <MessageSquare className="h-3 w-3" />
          Voir la conversation
          <span aria-hidden>→</span>
        </button>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="ml-2 p-1 -m-1 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark rounded"
          title="Réduire"
          aria-label="Réduire la réponse"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 -m-1 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark rounded"
          title="Masquer jusqu'à la prochaine réponse"
          aria-label="Masquer la réponse"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </header>

      {voixDistinct && (
        <blockquote className="mt-2.5 mb-3 pl-3 border-l-4 border-chip-orange text-base italic text-ink dark:text-ink-dark leading-snug">
          {voix}
        </blockquote>
      )}

      {/* DETAILS — always proper markdown. Clamp + fade when long. */}
      <div className="relative">
        <div
          ref={bodyRef}
          style={
            clamped
              ? {
                  maxHeight: `${COLLAPSED_HEIGHT_PX}px`,
                  overflow: "hidden",
                }
              : undefined
          }
        >
          <AgentMarkdown markdown={details || voix} className="max-w-none" />
        </div>
        {clamped && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white dark:from-surface-dark via-white/90 dark:via-surface-dark/90 to-transparent"
          />
        )}
      </div>

      {overflowing && (
        <div className="mt-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-ink dark:text-ink-dark hover:underline"
          >
            {expanded ? "Réduire" : "Tout afficher ici"}
          </button>
          <button
            type="button"
            onClick={onOpenDrawer}
            className="inline-flex items-center gap-1 text-xs text-ink dark:text-ink-dark hover:underline"
          >
            Voir la réponse complète
            <span aria-hidden>→</span>
          </button>
        </div>
      )}
    </section>
  );
}
